<template>
    <div class="sales-department-overview">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Обзор отдела продаж</h6>
                <div class="d-flex align-items-center">
                    <b-form-select 
                        v-model="selectedPeriod" 
                        size="sm" 
                        style="max-width: 250px; margin-right: 10px;" 
                        :options="availableQuarters"
                        @change="onPeriodChange"
                    ></b-form-select>
                    <b-button 
                        size="sm" 
                        variant="outline-primary"
                        @click="refreshData"
                        :disabled="loading"
                        class="mr-2"
                    >
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                    </b-button>
                    <b-button 
                        v-if="canFixQuarter"
                        size="sm" 
                        variant="warning"
                        @click="fixQuarter"
                        :disabled="loading || isQuarterFixed || fixingQuarter"
                        class="mr-2"
                    >
                        <i class="fas fa-lock" :class="{ 'fa-spin': fixingQuarter }"></i>
                        {{ isQuarterFixed ? 'Зафиксировано' : (fixingQuarter ? 'Фиксация...' : 'Зафиксировать') }}
                    </b-button>
                </div>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center py-4">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Загрузка...</span>
                    </div>
                    <p class="mt-2">Загрузка данных отдела...</p>
                </div>

                <div v-else>
                    <div class="department-stats mb-4">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="stat-card text-center">
                                    <div class="stat-value text-success">{{ formatCurrency(departmentStats.totalMargin) }}</div>
                                    <div class="stat-label">Общая маржинальная прибыль</div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="stat-card text-center">
                                    <div class="stat-value text-secondary">{{ formatCurrency(departmentStats.totalSuperIncome) }}</div>
                                    <div class="stat-label">Сверхокупаемость</div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="stat-card text-center">
                                    <div class="stat-value text-primary">{{ formatCurrency(departmentStats.totalBonuses) }}</div>
                                    <div class="stat-label">Общие бонусы (10%)</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="employees-table">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="thead-light">
                                    <tr>
                                        <th style="height: 36px;">Сотрудник</th>
                                        <th style="height: 36px;">Маржинальность</th>
                                        <th style="height: 36px;">Сверхокупаемость</th>
                                        <th style="height: 36px;">Бонус (10%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="employee in employees" :key="employee.id" @click="openUserSidebar(employee)" class="employee-row">
                                        <td>
                                            <div class="employee-info">
                                                <div class="employee-name">{{ employee.name }}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <span class="text-success font-weight-bold">
                                                {{ formatCurrency(employee.margin) }}
                                            </span>
                                        </td>
                                        <td>
                                            <span class="text-secondary font-weight-bold">
                                                {{ formatCurrency(employee.superIncome) }}
                                            </span>
                                        </td>
                                        <td>
                                            <span class="text-primary font-weight-bold">
                                                {{ formatCurrency(employee.bonus) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- График маржинальности отдела -->
                    <div class="charts-section mt-4">
                        <div class="card bdr-tabs">
                            <div class="card-header">
                                <h6 class="mb-0">Маржинальность отдела по месяцам</h6>
                            </div>
                            <div class="card-body">
                                <MotivationMarginalityChart 
                                    :key="`sales-marginality-${chartKey}`"
                                    :rawChartData="marginalityChartData"
                                    :loading="marginalityChartLoading"
                                    title="Общая маржинальность (₽)"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-4">
                        <small class="text-muted">
                            <strong>Кварталы считаются по следующим месяцам:</strong><br>
                            Q1: декабрь, январь, февраль | 
                            Q2: март, апрель, май | 
                            Q3: июнь, июль, август | 
                            Q4: сентябрь, октябрь, ноябрь
                        </small>
                    </div>
                </div>
            </div>
        </div>

        <!-- Сайдбар с персональной статистикой пользователя -->
        <div v-if="sidebarOpen" class="user-sidebar-overlay" @click="closeUserSidebar">
            <div class="user-sidebar" @click.stop>
                <div class="user-sidebar-header">
                    <h5 class="mb-0">Персональная статистика</h5>
                    <button type="button" class="btn-close" @click="closeUserSidebar">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="user-sidebar-body">
                    <SalesPersonalStats
                        v-if="selectedUser"
                        :user-id="selectedUser.id"
                        :period="selectedPeriod"
                        :department-id="departmentId"
                        :hide-motivation-text="true"
                        :open-in-new-window="true"
                    />
                </div>
            </div>
        </div>
    </div>
    </template>

<script>
import { API_MOTIVATION2025_SALES, API_MOTIVATION2025_CHARTS_SALES_MARGINALITY, API_MOTIVATION2025_FIX_QUARTER } from '@/constants/api'
import MotivationMarginalityChart from '@components/charts/MotivationMarginalityChart.vue'
import SalesPersonalStats from './SalesPersonalStats.vue'

export default {
    name: 'SalesDepartmentOverview',
    components: { SalesPersonalStats, MotivationMarginalityChart },
    props: {
        period: {
            type: String,
            default: 'current'
        },
        departmentId: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            loading: false,
            selectedPeriod: this.period || this.getCurrentQuarter(),
            employees: [],
            departmentStats: {
                totalMargin: 0,
                totalSuperIncome: 0,
                totalBonuses: 0
            },
            isQuarterFixed: false,
            fixingQuarter: false,
            sidebarOpen: false,
            selectedUser: null,
            marginalityChartLoading: false,
            marginalityChartData: [],
            chartKey: 0
        }
    },
    computed: {
        availableQuarters() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            
            const quarters = []
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал для выбора
            
            // Перебираем годы начиная с 2025
            for (let y = minYear; y <= year; y++) {
                if (y === minYear && y === year) {
                    // Для 2025 года, когда это текущий год
                    // Q4 доступен начиная с сентября
                    if (month >= 9) {
                        quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                    }
                    // Если мы в декабре 2025, добавляем Q1 2026
                    if (month === 12) {
                        quarters.push({ value: `${y + 1}-Q1`, text: `Q1 ${y + 1} (декабрь, январь, февраль)` })
                    }
                } else if (y === minYear) {
                    // Для 2025 года, когда это прошлый год - все кварталы
                    quarters.push({ value: `${y}-Q1`, text: `Q1 ${y} (декабрь, январь, февраль)` })
                    quarters.push({ value: `${y}-Q2`, text: `Q2 ${y} (март, апрель, май)` })
                    quarters.push({ value: `${y}-Q3`, text: `Q3 ${y} (июнь, июль, август)` })
                    quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                } else if (y === year) {
                    // Для текущего года (после 2025) - все актуальные кварталы
                    // Q1: декабрь предыдущего года, январь и февраль текущего
                    if (month === 12 || month === 1 || month === 2) {
                        const q1Year = month === 12 ? y + 1 : y
                        quarters.push({ value: `${q1Year}-Q1`, text: `Q1 ${q1Year} (декабрь, январь, февраль)` })
                    } else if (month >= 3) {
                        quarters.push({ value: `${y}-Q1`, text: `Q1 ${y} (декабрь, январь, февраль)` })
                    }
                    if (month >= 3) quarters.push({ value: `${y}-Q2`, text: `Q2 ${y} (март, апрель, май)` })
                    if (month >= 6) quarters.push({ value: `${y}-Q3`, text: `Q3 ${y} (июнь, июль, август)` })
                    if (month >= 9) quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                } else {
                    // Для прошлых лет (после 2025) - все кварталы
                    quarters.push({ value: `${y}-Q1`, text: `Q1 ${y} (декабрь, январь, февраль)` })
                    quarters.push({ value: `${y}-Q2`, text: `Q2 ${y} (март, апрель, май)` })
                    quarters.push({ value: `${y}-Q3`, text: `Q3 ${y} (июнь, июль, август)` })
                    quarters.push({ value: `${y}-Q4`, text: `Q4 ${y} (сентябрь, октябрь, ноябрь)` })
                }
            }
            
            // Разворачиваем массив, чтобы текущий квартал был сверху
            return quarters.reverse()
        }
    },
    mounted() {
        if (!this.selectedPeriod || this.selectedPeriod === 'current') {
            this.selectedPeriod = this.getCurrentQuarter()
        }
        this.loadData()
    },
    methods: {
        canFixQuarter() {
            const user = this.$store.getters['auth/currentUser']
            return user && user.role && (
                user.role.includes('administrator') || 
                user.role.includes('president') ||
                user.role.includes('accountant') ||
                user.role.includes('head-accountant')
            )
        },
        onPeriodChange() {
            this.loadData()
            this.loadMarginalityChartData()
        },
        refreshData() {
            this.loadData()
            this.loadMarginalityChartData()
        },
        loadData() {
            this.loading = true
            const params = {
                quarter: this.selectedPeriod,
                department_id: this.departmentId
            }
            axios.get(API_MOTIVATION2025_SALES, { params })
                .then(response => {
                    if (response.data.success) {
                        this.processData(response.data.data)
                        this.isQuarterFixed = !!response.data.is_fixed
                        this.$nextTick(() => { this.chartKey++ })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(err => console.error('Error loading sales data:', err))
                .finally(() => this.loading = false)
            // график
            this.loadMarginalityChartData()
        },
        loadMarginalityChartData() {
            this.marginalityChartLoading = true
            const params = { quarter: this.selectedPeriod, department_id: this.departmentId }
            axios.get(API_MOTIVATION2025_CHARTS_SALES_MARGINALITY, { params })
                .then(res => {
                    if (res.data.success) {
                        this.marginalityChartData = res.data.data
                        this.$nextTick(() => { this.chartKey++ })
                    }
                })
                .catch(err => console.error('Error loading sales marginality chart:', err))
                .finally(() => { this.marginalityChartLoading = false })
        },
        processData(data) {
            this.employees = data.map(user => ({
                id: user.user_id,
                name: user.user_name,
                margin: user.margin.total_margin,
                superIncome: user.super_income,
                bonus: user.bonus
            }))
            if (this.employees.length > 0) {
                this.departmentStats.totalMargin = this.employees.reduce((s, e) => s + Number(e.margin || 0), 0)
                this.departmentStats.totalSuperIncome = this.employees.reduce((s, e) => s + Number(e.superIncome || 0), 0)
                this.departmentStats.totalBonuses = this.employees.reduce((s, e) => s + Number(e.bonus || 0), 0)
            } else {
                this.departmentStats.totalMargin = 0
                this.departmentStats.totalSuperIncome = 0
                this.departmentStats.totalBonuses = 0
            }
        },
        fixQuarter() {
            if (!this.canFixQuarter()) {
                this.$toast && this.$toast.error('У вас нет прав для фиксации кварталов')
                return
            }
            if (this.isQuarterFixed) {
                this.$toast && this.$toast.info('Квартал уже зафиксирован')
                return
            }
            this.fixingQuarter = true
            const data = { quarter: this.selectedPeriod, department_id: this.departmentId }
            axios.post(API_MOTIVATION2025_FIX_QUARTER, data)
                .then(response => {
                    if (response.data.success) {
                        this.isQuarterFixed = true
                        this.$toast && this.$toast.success(response.data.message || 'Квартал зафиксирован')
                    } else {
                        this.$toast && this.$toast.error(response.data.message || 'Ошибка при фиксации квартала')
                    }
                })
                .catch(() => {
                    this.$toast && this.$toast.error('Ошибка соединения с сервером')
                })
                .finally(() => { this.fixingQuarter = false })
        },
        openUserSidebar(employee) {
            this.selectedUser = employee
            this.sidebarOpen = true
        },
        closeUserSidebar() {
            this.sidebarOpen = false
            this.selectedUser = null
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                minimumFractionDigits: 0
            }).format(amount || 0)
        },
        getCurrentQuarter() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал
            
            let currentQuarter = ''
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                currentQuarter = month === 12 ? `${year + 1}-Q1` : `${year}-Q1`
            } else if (month >= 3 && month <= 5) {
                currentQuarter = `${year}-Q2`
            } else if (month >= 6 && month <= 8) {
                currentQuarter = `${year}-Q3`
            } else if (month >= 9 && month <= 11) {
                currentQuarter = `${year}-Q4`
            }
            
            // Проверяем, что квартал не раньше Q4 2025
            const [qYear, qNum] = currentQuarter.split('-Q')
            const quarterNum = parseInt(qNum)
            
            if (qYear < minYear || (qYear == minYear && quarterNum < minQuarter)) {
                return '2025-Q4' // Возвращаем минимальный доступный квартал
            }
            
            return currentQuarter || '2025-Q4'
        }
    }
}
</script>

<style scoped>
.stat-card {
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    background-color: #f8f9fa;
}
.stat-value { font-size: 1rem; font-weight: bold; margin-bottom: 0.25rem; }
.stat-label { font-size: 0.7rem; color: #6c757d; }
.employee-info { display: flex; flex-direction: column; }
.employee-name { font-weight: 500; }
.table th { border-top: none; font-weight: 600; color: #495057; }
.table td { vertical-align: middle; }

/* Стили сайдбара (как в ТО) */
.employee-row { cursor: pointer; transition: background-color 0.2s ease; }
.employee-row:hover { background-color: #f8f9fa; }
.employee-row .employee-name { font-weight: 600; }
.employee-row:hover .employee-name { color: #0D6247; font-weight: 600; }

.user-sidebar-overlay { position: fixed; top:0; left:0; right:0; bottom:0; background-color: rgba(0,0,0,0.5); z-index:1050; display:flex; justify-content:flex-end; align-items:stretch; }
.user-sidebar { width:80%; max-width:1200px; background-color:#fff; box-shadow:-2px 0 10px rgba(0,0,0,0.1); display:flex; flex-direction:column; height:100vh; overflow:hidden; }
.user-sidebar-header { padding:1rem 1.5rem; border-bottom:1px solid #dee2e6; background-color:#f8f9fa; display:flex; justify-content:space-between; align-items:center; flex-shrink:0; }
.btn-close { background:none; border:none; font-size:1.25rem; color:#6c757d; cursor:pointer; padding:0.25rem; border-radius:0.25rem; transition:all 0.2s ease; }
.btn-close:hover { color:#495057; background-color:#e9ecef; }
.user-sidebar-body { flex:1; overflow-y:auto; padding:0; }

/* Стили для скроллбара */
.user-sidebar-body::-webkit-scrollbar {
    width: 6px;
}

.user-sidebar-body::-webkit-scrollbar-track {
    border-radius: 3px;
}

.user-sidebar-body::-webkit-scrollbar-thumb {
    border-radius: 3px;
}

@media (max-width: 768px) { .user-sidebar { width:100%; max-width:none; } }
</style>

